import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { useAuth, scopeFromRole } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import type { RoleKey } from "@/shared/lib/rbac";
import type { CapabilityCluster } from "@/entities/capability";
import { visibleByPerms } from "@/widgets/appSidebarNav";
import { useClusterCards } from "@/pages/workbench/useClusterCards";

import { RAIL_SECTIONS, SUPPORT_GROUPS, resolveRailKey } from "./shellNav";
import { buildStrip } from "./shellStrip";

type SupportKey = keyof typeof SUPPORT_GROUPS;

interface ShellContextStripProps {
  /** inline = 顶栏 header 中段(≥lg 才出);row = 顶栏下方独立一行(<lg 才出)。 */
  variant: "inline" | "row";
}

// ★不带图标、内边距收紧:组织区经理号 8 项 / 管理员号 9 项要在 1280 宽的顶栏中段一行放下
//   (带 14px 图标时 8 项就溢出横滑,末两项被裁掉 —— 0917 截图实测)。图标左轨已经有,这里是文字导航。
const PILL =
  "flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[13px] transition-colors";
const PILL_ON = "border-primary/45 bg-primary/12 font-semibold text-primary";
const PILL_OFF = "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground";

/**
 * 壳的上下文条 —— 顶栏中段**永远显示「当前轨项的下一层」**(2026-09-17 导航重排)。
 *
 * ★起因:左轨与顶栏重复。顶栏中段那排「首页 + 5 群」是 0817 把 74px 轨**同一层**镜像上去的,
 *   两处画同一层 = 用户读到重复而非层级。本条改画下一层:
 *   能力群 → 群名面包屑 + 该群**已开且能进**的舱;支撑区 → 区名 + 该区页面;首页 → 什么都不画。
 * ★舱的单源 = `useClusterCards().forCluster`(与首页卡墙 / 群页 / ⌘K 逐字同源),门控口径不在这里重写。
 *   占位舱与「active 但无落点」的舱不上条(解析在 {@link buildStrip},可裸单测)。
 * ★平台超管不出本条(平台走 AppSidebar 全树),顺带让 `useClusterCards` 的两个 query 不发请求。
 * ★两个变体:≥lg 内嵌 header 中段、<lg 落成 header 下方独立一行(顶栏塞不下 5 张舱 pill)。
 *   AdminLayout 同时挂两个实例,靠 CSS 断点二选一;两实例共用同一批 react-query key,请求会去重。
 */
export function ShellContextStrip({ variant }: ShellContextStripProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { permissions, user } = useAuth();
  const { isImpersonating } = useImpersonationOptional();

  const role = ((permissions?.role as RoleKey | undefined) ?? user?.role) as RoleKey | undefined;
  const scope = role ? scopeFromRole(role) : user?.scope;
  // OEM W3:渠道管理员(PARTNER)同平台超管走后台壳,不出租户上下文条、不拉租户卡墙。
  const isPlatform = scope === "PLATFORM" || scope === "PARTNER";

  // hooks 无条件调用;平台超管靠 enabled=false 不发请求(口径照 CommandPalette)。
  const { forCluster } = useClusterCards({ enabled: !isPlatform });

  const railKey = resolveRailKey(pathname);
  const section = RAIL_SECTIONS.find((s) => s.key === railKey);

  const model = isPlatform
    ? null
    : buildStrip({
        railKey,
        pathname,
        t: (key, options) => t(key, options),
        cards: section?.kind === "cluster" ? forCluster(railKey as CapabilityCluster) : null,
        support:
          section?.kind === "support"
            ? visibleByPerms(
                SUPPORT_GROUPS[railKey as SupportKey],
                permissions?.modules,
                scope,
                role,
                isImpersonating
              )
            : [],
      });

  if (!model) return null;

  return (
    <nav
      aria-label={t("shell.stripAria")}
      data-shell-strip={variant}
      className={cn(
        "items-center gap-1 overflow-x-auto",
        variant === "inline"
          ? // 顶栏中段:≥lg 才出;超宽不换行,自己横滑(header 高度 56 固定,不许被撑开)。
            "hidden min-w-0 lg:flex"
          : // <lg 独立一行:沿用支撑区二级条那套容器(sticky + 毛玻璃 + 下边框)。
            "sticky top-0 z-20 flex border-b bg-card/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-card/80 lg:hidden"
      )}
    >
      {model.crumb.route ? (
        <Link
          to={model.crumb.route}
          className="shrink-0 whitespace-nowrap text-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
        >
          {model.crumb.label}
        </Link>
      ) : (
        // 支撑区没有「区首页」,面包屑只报位置不可点。
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-muted-foreground">
          {model.crumb.label}
        </span>
      )}
      <span aria-hidden="true" className="shrink-0 text-xs text-muted-foreground/50">
        ›
      </span>

      {model.items.map((it) => {
        const body = (
          <>
            {it.label}
            {it.externalUrl && <span className="ml-1 text-[11px] leading-none">↗</span>}
          </>
        );
        // 外站舱(漫剧工坊)新开标签页,不走 SPA 路由 —— 与群页卡 openOf 同一条口径。
        return it.externalUrl ? (
          <button
            key={it.key}
            type="button"
            onClick={() => window.open(it.externalUrl, "_blank", "noopener,noreferrer")}
            className={cn(PILL, PILL_OFF)}
          >
            {body}
          </button>
        ) : (
          <Link
            key={it.key}
            to={it.route ?? "/"}
            aria-current={it.active ? "page" : undefined}
            className={cn(PILL, it.active ? PILL_ON : PILL_OFF)}
          >
            {body}
          </Link>
        );
      })}

      {/* 一个已开舱都没有的群(如运营):说清「即将开放」,别留一条只有面包屑的空条。 */}
      {model.kind === "cluster" && model.items.length === 0 && (
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          {t("shell.stripNoCabin")}
        </span>
      )}
    </nav>
  );
}
